import React, { useContext } from 'react';
import { makeStyles, createMuiTheme, MuiThemeProvider } from '@material-ui/core/styles';
import { Accordion, AccordionSummary, AccordionDetails, Button, CircularProgress } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import { AuthStatusContext } from '@trp-individual-investor-adv-disc/adv-shared';
import ToggleCarat from '../../images/ToggleCarat';
import { footerContent, footerTitle } from '../../utils/Content';
import { submitIP } from '../Hub/HubUtils';

const expansionPanelTheme = createMuiTheme({
    overrides: {
        MuiAccordionSummary: {
            content: {
                "&$expanded": {
                    margin: 0
                }
            },
            root: {
                "&$expanded": {
                    minHeight: '32px'
                }
            }
        },
        MuiAccordion: {
            rounded: {
                "&:last-child": {
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                },

                "&:first-child": {
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: 0
                },
            }
        }
    },
});

const useStyles = makeStyles((theme) => ({

    '@global': {
        'MuiAccordion-root': {
            expanded: {
                minHeight: '32px'
            }
        }
    },
    root: {
        position: 'fixed',
        bottom: 0,
        zIndex: 1299
    },
    heading: {
        fontSize: 24,
        fontFamily: 'Nimbus Sans Bold',
        color: 'white',
        maxWidth: '1166px'
    },
    mobileHeading: {
        fontSize: 20,
        fontFamily: 'Nimbus Sans Bold',
        color: 'white',
        [theme.breakpoints.down('sm')]: {
            fontSize: 16,
        }
    },
    panel: {
        backgroundColor: '#002F48'
    },
    mobileSummary: {
        width: '50%',
        margin: '0 auto',
        maxWidth: '1166px',
        paddingTop: '16px',
        paddingBottom: '16px',
        paddingLeft: 0,
        paddingRight: 0
    },
    summary: {
        padding: 16,
        maxWidth: '1170px',
        margin: '0 auto'
    },
    buttonContainer: {
        margin: '0 auto',
        maxWidth: '1170px',
        padding: '0 16px 16px 16px',
    },
    details: {
        margin: '0 auto',
        maxWidth: '1166px',
        color: 'white',
        fontSize: 14,
        padding: '24px 12px 24px 12px',
        fontFamily: 'Nimbus Sans Reg',

    },
    mobileDetails: {
        margin: '0 auto',
        maxWidth: '1170px',
        color: 'white',
        fontSize: 14,
        padding: '0px 16px 32px 16px',
        fontFamily: 'Nimbus Sans Reg',

    },
    contentContainer: {
        display: 'flex',
        flexWrap: 'wrap'
    },
    contentDetails: {
        fontFamily: 'Nimbus Sans Reg',
        width: '100%',
        fontSize: 14
    },
    primaryButton: {
        boxShadow: 'none',
        background: '#007DB3',
        padding: '8px 16px',
        minWidth: '225px',
        '&:hover': {
            backgroundColor: 'white',
            color: '#007DB3'
        },
        fontSize: 16,
        fontFamily: 'Nimbus Sans Bold',
        color: 'white',
        textTransform: 'none',
        maxHeight: '40px',
        alignSelf: 'center',

        [theme.breakpoints.down('sm')]: {
            width: '100%',

        },
    },
    disabledButton: {
        boxShadow: 'none',
        backgroundColor: '#929BA3',
        padding: '8px 16px',
        minWidth: '225px',
        fontSize: 16,
        fontFamily: 'Nimbus Sans Bold',            
        color: '#D3D7DA !important',
        textTransform: 'none',
        maxHeight: '40px',
        alignSelf: 'center',
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        }
    }
}));

const StickyFooter = ({ link = '/', hasIneligibleExternalAccounts, hasIneligibleBrokerage, disabled }) => {
    const classes = useStyles();

    const { applicationConfig } = useContext(AuthStatusContext).state;

    // calculate viewport width minus scrollbar width if there is one
    const windowWidth = window && window.innerWidth ? window.innerWidth : 0;
    const rootDiv = document.querySelector('#root');
    const rootWidth = rootDiv && rootDiv.offsetWidth ? rootDiv.offsetWidth : 0;
    const viewportWidthMinusScrollbar = `calc(100vw - ${windowWidth - rootWidth}px)`;

    const [expanded, setExpanded] = React.useState(windowWidth >= 960 ? 'panel1' : false);
    const [isLoading, setIsLoading] = React.useState(false);

    const handleChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    const handleSubmit = () => {
        setIsLoading(true);
        submitIP(applicationConfig, link);
    };
    /* eslint-disable  react/no-danger */
    return (
        <MuiThemeProvider theme={expansionPanelTheme}>
            <div className={classes.root} style={{ width: viewportWidthMinusScrollbar }} data-metrics-location="FFTR" id='ftr-container' data-html2canvas-ignore='true'>
                <Accordion className={classes.panel} expanded={expanded === 'panel1'} onChange={handleChange('panel1')}>
                    <AccordionSummary
                        expandIcon={<ToggleCarat style={{ color: 'white', fontSize: 32, }} id='ftr-tgl' />}
                        aria-controls="footer-content"
                        id="footer-panel"
                        className={classes.summary}
                    >
                        <Typography className={classes.mobileHeading} id='sticky-ftr-header'>
                            {footerTitle.regAccContent}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails className={classes.mobileDetails}>
                        <Typography className={classes.contentDetails}>
                        <span
                                dangerouslySetInnerHTML={{
                                    __html: footerContent
                                }}
                                id='footer-content'
                            />
                        </Typography>
                    </AccordionDetails>
                    <div className={classes.buttonContainer} id='ftr-mobile-btn-container'>
                        <Button     
                            classes={{ root: classes.primaryButton, disabled: classes.disabledButton, }} 
                            onClick={() => handleSubmit()} 
                            disabled={disabled || hasIneligibleExternalAccounts || hasIneligibleBrokerage || isLoading}
                            id='ftr-btn-mobile'
                        >
                            <div>Begin Account Opening {' '}
                                {isLoading && React.cloneElement(
                                    <CircularProgress
                                        size={20}
                                        id='loading-spinner'
                                        style={{
                                            color: '#05C3DE',
                                            marginLeft: '16px',
                                            verticalAlign: 'sub',
                                            animation: 'none'
                                        }}
                                    />)
                                }
                            </div>
                        </Button>
                    </div>
                </Accordion>
            </div>
        </MuiThemeProvider >
    );
}

export default StickyFooter;
